import CloudflareImagesImage from "@common/components/CloudflareImagesImage";
import {
  cleanParagraphTags,
  GetBackgroundStyle,
  IsNodeStructure,
} from "@talladega/helpers";
import classNames from "classnames";
import { useState } from "react";
import { ArrowRightIcon } from "@talladega/icons";
import {
  AcfIconPosition,
  ActionButton,
  MultiCardSectionLayout,
} from "@talladega/types";
import { CmsHeading } from "@talladega/cms";
import CmsButton from "./CmsButton";
import CmsButtonModalContainer from "./CmsButtonModalContainer";

const MultiCardSection = ({
  sectionHeading,
  sectionSubheading,
  cardLayout,
  button,
  background,
  htmlIdRef,
}: MultiCardSectionLayout) => {
  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    <div
      id="cms-multi-card-section"
      className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div className="mx-auto max-w-page px-4 pb-6 pt-10 lg:px-5 lg:py-16">
        <article>
          {sectionHeading && (
            <div className="md:text-center">
              <CmsHeading
                headingLevel={sectionHeading.headingGroup?.headingLevel}
                textSize={sectionHeading.headingGroup?.textSize}
                text={sectionHeading.headingGroup?.text}
                htmlId={sectionHeading.headingGroup?.htmlId}
              />
            </div>
          )}

          {sectionSubheading && (
            <div className="mt-4 md:text-center">
              <CmsHeading
                headingLevel={sectionSubheading.headingGroup?.headingLevel}
                textSize={sectionSubheading.headingGroup?.textSize}
                text={sectionSubheading.headingGroup?.text}
                htmlId={sectionSubheading.headingGroup?.htmlId}
              />
            </div>
          )}
        </article>
        <div
          className={classNames(
            "mx-auto grid max-w-xl grid-flow-row grid-cols-1 gap-x-6 gap-y-4 pt-12 text-center xl:gap-x-8 xl:gap-y-10",
            {
              "sm:max-w-3xl sm:grid-cols-2": cardLayout.length === 2,
              "sm:max-w-3xl sm:grid-cols-2 lg:max-w-none lg:grid-cols-3":
                cardLayout.length >= 3,
            }
          )}
        >
          {cardLayout.map((card) => {
            const { altText, sourceUrl } =
              (IsNodeStructure(card?.cardIcon)
                ? card?.cardIcon.node
                : card?.cardIcon) ?? {};

            const { cardIconPosition } = card;

            return (
              <div
                key={card.cardLabel + card.cardSubLabel}
                className={classNames(
                  "group flex h-auto w-full flex-1 gap-3 rounded-lg bg-white p-4 shadow-elevation-01 transition md:p-8",
                  {
                    "flex-row": cardIconPosition === AcfIconPosition.Left,
                    "flex-col": cardIconPosition !== AcfIconPosition.Left,
                  }
                )}
              >
                <div
                  className={classNames(
                    "flex mt-2 text-gray-500 transition duration-300 ease-in-out",
                    {
                      "justify-center":
                        cardIconPosition === AcfIconPosition.TopCenter,
                      "justify-start":
                        cardIconPosition === AcfIconPosition.TopLeft,
                      "w-full": cardIconPosition !== AcfIconPosition.Left,
                      "sm:mt-8": cardIconPosition === AcfIconPosition.Left,
                    }
                  )}
                >
                  {sourceUrl && (
                    <div className="relative h-16 w-16" key={`${altText}`}>
                      <CloudflareImagesImage
                        src={sourceUrl}
                        alt={altText}
                        fill
                        className="items-center justify-center text-center"
                      />
                    </div>
                  )}
                </div>

                <div
                  className={classNames("flex flex-col gap-3", {
                    "text-left":
                      cardIconPosition === AcfIconPosition.Left ||
                      cardIconPosition === AcfIconPosition.TopLeft,
                  })}
                >
                  <div className="flex w-full flex-col justify-center px-3 md:hidden">
                    <div
                      className="text-lg font-semibold"
                      key={card.cardLabel}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: cleanParagraphTags(card.cardLabel),
                      }}
                    />
                    <div
                      className="pt-2 text-sm font-normal [&>ul]:list-disc [&>ul]:pl-4"
                      key={card.cardSubLabel}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: cleanParagraphTags(card.cardSubLabel),
                      }}
                    />
                  </div>

                  <h5
                    className="hidden pt-4 transition duration-300 ease-in-out md:block"
                    key={`${card.cardLabel}-md`}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: cleanParagraphTags(card.cardLabel),
                    }}
                  />
                  <div
                    className="text-md hidden pb-8 pt-3 font-normal md:block [&>ul]:list-disc [&>ul]:px-5 [&>ul]:text-start"
                    key={`${card.cardSubLabel}-md`}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: cleanParagraphTags(card.cardSubLabel),
                    }}
                  />
                  {card.button && !card.button.isHidden && (
                    <CmsButton
                      button={card.button}
                      rightIcon={<ArrowRightIcon />}
                      setButtonModalState={setButtonModalState}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-center">
          {!button.isHidden && (
            <div className="w-56 pt-8 md:pt-12">
              <CmsButton
                className="w-full"
                button={button}
                rightIcon={<ArrowRightIcon />}
                setButtonModalState={setButtonModalState}
                htmlIdRef={htmlIdRef}
              />
            </div>
          )}
        </div>
        {buttonModalState.show && (
          <CmsButtonModalContainer buttonModalState={buttonModalState} />
        )}
      </div>
    </div>
  );
};

export default MultiCardSection;
